import Controller from './adapters/Controller'
import Presenter from './adapters/Presenter'
import {
  GetAreaEquipmentsUseCase,
  GetDestinationFavoritePeriodUseCase,
  GetHouseDetailsUseCase,
  GetHouseIncludedServices,
  GetHouseTruthLinksUseCase,
  GetHouseKeyFeaturesUseCase,
  GetHousePeriodsAndBookingsUseCase,
  GetHousePeriodsByIdUseCase,
  GetHousePeriodsUseCase,
  GetHousePhotosUseCase,
  GetNbHiddenHousesUseCase,
  GetTopDestinationsUseCase,
  MakeHouseRequestUseCase,
  OrderRetrievedFacetsUseCase,
  SearchHousesAndDestinationsUseCase,
  SearchHousesMapUseCase,
  SearchHousesWithDatesUseCase,
  SearchHousesWithoutDatesUseCase,
  SearchHousesWithoutDatesV2UseCase,
  UpdateInquiryUseCase,
  GetDestinationPhotoUseCase,
  GetSimilarHousesPricesByPeriodUseCase,
  SearchDestinationsUseCase,
  GetHouseNextValidPeriodUseCase,
  SearchHousesSplitMapUseCase,
  GetHouseSplitMapUseCase,
  SearchUnknownDestinationUseCase,
} from './usecases'

import { algolia } from '../../services/algolia'
import type {
  GeoCoding,
  HouseRepository,
  RequestRepository,
} from 'lc-repository'
import type { AppsSource } from '../../types'

export type SearchBuildType = {
  repository: {
    houses: ReturnType<typeof HouseRepository>
    request: ReturnType<typeof RequestRepository>
    geocoding?: GeoCoding
  }
}

export const searchBuild = <S extends AppsSource = 'web'>(
  { repository }: SearchBuildType,
  source?: S,
) => {
  const { houses, request, geocoding } = repository
  const commonRepo = {
    algolia,
    getHousePeriodsIds: houses.periods.getHousePeriodsIds,
    getHousePeriods: houses.periods.getHousePeriods,
  }
  const presenter = new Presenter<S>(source)
  const controller = new Controller(
    {
      getHouseDetails: new GetHouseDetailsUseCase(houses),
      getHouseAreaEquipments: new GetAreaEquipmentsUseCase(houses),
      getHouseKeyFeatures: new GetHouseKeyFeaturesUseCase(houses),
      getHouseIncludedServices: new GetHouseIncludedServices({
        getHouseIncludedServices: houses.getHouseIncludedServices,
      }),
      getHouseTruthLinks: new GetHouseTruthLinksUseCase({
        getHouseTruthLinks: houses.getHouseTruthLinks,
      }),
      getHousePeriods: new GetHousePeriodsUseCase({
        getHousePeriods: houses.periods.getHousePeriods,
      }),
      getHousePeriodsById: new GetHousePeriodsByIdUseCase({
        getHousePeriodsById: houses.periods.getHousePeriodsById,
      }),
      getHousePhotos: new GetHousePhotosUseCase(houses),
      getHousePeriodsAndBookings: new GetHousePeriodsAndBookingsUseCase({
        getBookingsByHouseId: houses.bookings.getBookingsByHouseId,
        getPeriodDurations: houses.periods.getPeriodDurations,
      }),
      getHouseSplitMap: new GetHouseSplitMapUseCase(commonRepo),
      getTopDestinations: new GetTopDestinationsUseCase(commonRepo),
      makeHouseRequest: new MakeHouseRequestUseCase(request.request),
      orderFacets: new OrderRetrievedFacetsUseCase(),
      searchHouses: {
        searchHousesWithoutDates: new SearchHousesWithoutDatesUseCase(
          commonRepo,
        ),
        searchHousesWithDates: new SearchHousesWithDatesUseCase(commonRepo),
      },
      searchHousesV2: {
        searchHousesWithoutDates: new SearchHousesWithoutDatesV2UseCase(
          commonRepo,
        ),
        searchHousesWithDates: new SearchHousesWithDatesUseCase(commonRepo),
      },
      searchHousesSplitMap: new SearchHousesSplitMapUseCase(commonRepo),
      searchHousesAndDestinations: new SearchHousesAndDestinationsUseCase(
        commonRepo,
      ),
      updateInquiry: new UpdateInquiryUseCase(request.request),
      getNbHiddenHouses: new GetNbHiddenHousesUseCase(commonRepo),
      searchHousesMap: new SearchHousesMapUseCase(commonRepo),
      getDestinationFavoritePeriod: new GetDestinationFavoritePeriodUseCase({
        getCountByPeriodTypes: houses.periods.getCountByPeriodTypes,
      }),
      getDestinationPhoto: new GetDestinationPhotoUseCase(houses),
      getSimilarHousesPricesByPeriod: new GetSimilarHousesPricesByPeriodUseCase(
        {
          getHousePeriodsById: houses.periods.getHousePeriodsById,
        },
      ),
      searchDestinations: new SearchDestinationsUseCase({
        algolia: commonRepo.algolia,
      }),
      getHouseNextValidPeriod: new GetHouseNextValidPeriodUseCase({
        getHouseNextValidPeriod: houses.periods.getHouseNextValidPeriod,
      }),
      ...(geocoding
        ? {
            searchUnknownDestination: new SearchUnknownDestinationUseCase(
              geocoding,
            ),
          }
        : null),
    },
    presenter,
  )

  return { controller, presenter }
}
